import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { api } from '../lib/api.ts';
import type { Me } from '../store/auth.ts';

export function Profile(): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [tgPending, setTgPending] = useState(false);
  const [tgError, setTgError] = useState<string | null>(null);
  // Saved signature is served from an auth-gated endpoint, so a plain
  // <img src> request gets a 401 and renders as a broken image. Pre-fetch
  // via axios (Bearer token is attached) and surface a blob URL instead.
  const [sigBlobUrl, setSigBlobUrl] = useState<string | null>(null);

  async function refreshMe(): Promise<void> {
    const r = await api.get<{ user: Me }>('/users/me');
    setMe(r.data.user);
  }

  async function linkTelegram(): Promise<void> {
    setTgError(null);
    setTgPending(true);
    try {
      const r = await api.post<{ url: string }>('/users/me/telegram/link');
      window.open(r.data.url, '_blank', 'noopener');
    } catch {
      setTgError('Could not generate Telegram link. Try again in a moment.');
    } finally {
      setTgPending(false);
    }
  }

  async function unlinkTelegram(): Promise<void> {
    setTgError(null);
    setTgPending(true);
    try {
      await api.delete('/users/me/telegram');
      await refreshMe();
    } catch {
      setTgError('Could not unlink Telegram.');
    } finally {
      setTgPending(false);
    }
  }

  useEffect(() => {
    void refreshMe();
  }, []);

  // Whenever the saved-signature URL changes, fetch it as a blob and swap in
  // an object URL so <img src> works without an auth header. Revoke the
  // previous URL to avoid leaking memory.
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    async function load(): Promise<void> {
      if (!me?.signatureImageUrl) { setSigBlobUrl(null); return; }
      try {
        const path = me.signatureImageUrl.replace(/^\/api/, '');
        const r = await api.get<Blob>(path, { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setSigBlobUrl(url);
      } catch {
        if (!cancelled) setSigBlobUrl(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [me?.signatureImageUrl]);

  if (!me) {
    return (
      <div className="page-narrow">
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading profile…</p>
      </div>
    );
  }

  const initials = (me.name ?? me.email).slice(0, 2).toUpperCase();

  return (
    <div className="page-narrow space-y-8">
      <header className="flex items-center gap-4">
        <div className="grid size-14 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))] text-lg font-medium">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">Account</p>
          <h1 className="font-sans font-normal text-[2rem] md:text-[2.5rem] leading-tight mt-1 text-ink truncate">
            {me.name ?? 'Your profile'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground truncate">{me.email} · {me.role}</p>
        </div>
      </header>

      <section className="rounded-2xl bg-[hsl(var(--surface-container-low))] border border-black/[0.04] p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-ink">Saved signature</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your signature is managed in <span className="font-medium text-foreground">Wapplify Office</span>.
            </p>
          </div>
          {me.signatureImageUrl && (
            <span className="pill pill-success shrink-0">On file</span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {me.signatureImageUrl && sigBlobUrl ? (
            <img
              src={sigBlobUrl}
              alt="Your saved signature"
              className="size-24 rounded-xl bg-muted object-contain ring-1 ring-border shrink-0"
            />
          ) : me.signatureImageUrl ? (
            <div className="size-24 rounded-xl bg-muted grid place-items-center text-xs text-muted-foreground ring-1 ring-border shrink-0">
              Loading…
            </div>
          ) : (
            <div className="size-24 rounded-xl bg-muted grid place-items-center text-sm text-muted-foreground ring-1 ring-border shrink-0">
              None
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-3">
            <p className="text-sm text-muted-foreground">
              {me.signatureImageUrl
                ? 'This is the signature used when you sign documents.'
                : 'You have no saved signature yet.'}
            </p>
            <a
              href="https://office.wapplify.app/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Manage your signature <Icon name="open_in_new" size={16} />
            </a>
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-[hsl(var(--surface-container-low))] border border-black/[0.04] p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-ink">Telegram notifications</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Get signing requests and signed PDFs delivered to Telegram.
            </p>
          </div>
          {me.telegramChatId && (
            <span className="pill pill-success shrink-0">Linked</span>
          )}
        </div>

        {me.telegramChatId ? (
          <div className="flex flex-wrap gap-2 items-center">
            {me.telegramLinkedAt && (
              <span className="text-sm text-muted-foreground tabular">
                Connected {new Date(me.telegramLinkedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
            <button onClick={() => void unlinkTelegram()} disabled={tgPending} className="btn-danger ml-auto">
              <Icon name="link_off" size={16} /> {tgPending ? 'Unlinking…' : 'Unlink'}
            </button>
            <button onClick={() => void refreshMe()} className="btn-ghost">
              <Icon name="refresh" size={16} /> Refresh
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              We&apos;ll open <code className="text-sm text-foreground bg-muted rounded px-1.5 py-0.5">@WapSignBot</code> — tap <b className="text-foreground">Start</b> to finish linking.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void linkTelegram()} disabled={tgPending} className="btn-primary">
                <Icon name="send" size={16} />
                {tgPending ? 'Opening…' : 'Link Telegram'}
              </button>
              <button onClick={() => void refreshMe()} className="btn-secondary">
                <Icon name="refresh" size={16} /> I&apos;ve linked it
              </button>
            </div>
          </div>
        )}
        {tgError && <p className="text-sm text-destructive">{tgError}</p>}
      </section>

      <section className="rounded-2xl bg-[hsl(var(--surface-container-low))] border border-black/[0.04] p-5 sm:p-6 space-y-3">
        <div>
          <h2 className="text-lg font-medium text-ink">Password</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your password is managed in <span className="font-medium text-foreground">Wapplify Office</span>.
          </p>
        </div>
        <a
          href="https://office.wapplify.app/profile"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary"
        >
          Manage your password <Icon name="open_in_new" size={16} />
        </a>
      </section>
    </div>
  );
}
